import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchAndFilter from '../components/SearchAndFilter';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';
import { toast } from 'react-toastify';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Payment statistics
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    successRate: 0,
    failedPayments: 0,
    pendingPayments: 0,
    refundedAmount: 0
  });

  useEffect(() => {
    fetchPayments();
    fetchPaymentStats();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockPayments = [
        {
          id: 1,
          transactionId: 'TXN-001',
          orderId: 'ORD-001',
          customer: { name: 'Alice Brown', email: 'alice@example.com' },
          amount: 87.48,
          method: 'credit_card',
          status: 'completed',
          gateway: 'stripe',
          gatewayTransactionId: 'pi_1234567890',
          createdAt: '2024-01-15T10:30:00Z',
          processedAt: '2024-01-15T10:31:00Z',
          fees: 2.62,
          netAmount: 84.86,
          currency: 'INR',
          refundedAmount: 0,
          refundReason: null
        },
        {
          id: 2,
          transactionId: 'TXN-002',
          orderId: 'ORD-002',
          customer: { name: 'Bob Johnson', email: 'bob@example.com' },
          amount: 63.99,
          method: 'paypal',
          status: 'completed',
          gateway: 'paypal',
          gatewayTransactionId: 'PAYID-1234567890',
          createdAt: '2024-01-14T14:20:00Z',
          processedAt: '2024-01-14T14:21:00Z',
          fees: 2.24,
          netAmount: 61.75,
          currency: 'INR',
          refundedAmount: 0,
          refundReason: null
        },
        {
          id: 3,
          transactionId: 'TXN-003',
          orderId: 'ORD-003',
          customer: { name: 'Carol Davis', email: 'carol@example.com' },
          amount: 50.50,
          method: 'bank_transfer',
          status: 'failed',
          gateway: 'razorpay',
          gatewayTransactionId: 'txn_1234567890',
          createdAt: '2024-01-13T16:45:00Z',
          processedAt: null,
          fees: 0,
          netAmount: 0,
          currency: 'INR',
          refundedAmount: 0,
          refundReason: null,
          failureReason: 'Insufficient funds'
        },
        {
          id: 4,
          transactionId: 'TXN-004',
          orderId: 'ORD-004',
          customer: { name: 'David Wilson', email: 'david@example.com' },
          amount: 125.00,
          method: 'credit_card',
          status: 'refunded',
          gateway: 'stripe',
          gatewayTransactionId: 'pi_0987654321',
          createdAt: '2024-01-12T09:15:00Z',
          processedAt: '2024-01-12T09:16:00Z',
          fees: 3.75,
          netAmount: 121.25,
          currency: 'INR',
          refundedAmount: 125.00,
          refundReason: 'Customer requested refund'
        }
      ];
      
      setPayments(mockPayments);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      // Mock stats - replace with actual API call
      const mockStats = {
        totalRevenue: 45680.50,
        totalTransactions: 1247,
        successRate: 94.2,
        failedPayments: 23,
        pendingPayments: 8,
        refundedAmount: 1250.00
      };
      
      setStats(mockStats);
    } catch (error) {
      toast.error('Failed to fetch payment statistics');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'secondary';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'credit_card':
        return <CreditCardIcon />;
      case 'paypal':
        return <PaymentIcon />;
      case 'bank_transfer':
        return <BankIcon />;
      case 'cash':
        return <MoneyIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setPaymentDialogOpen(true);
  };

  const handleRefundPayment = (payment) => {
    setSelectedPayment(payment);
    setRefundAmount(payment.amount.toString());
    setRefundDialogOpen(true);
  };

  const handleProcessRefund = async () => {
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setPayments(prev => prev.map(payment => 
        payment.id === selectedPayment.id 
          ? { 
              ...payment, 
              status: 'refunded',
              refundedAmount: parseFloat(refundAmount),
              refundReason: refundReason,
              updatedAt: new Date().toISOString()
            }
          : payment
      ));
      
      toast.success('Refund processed successfully');
      setRefundDialogOpen(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (error) {
      toast.error('Failed to process refund');
    }
  };

  const handleRetryPayment = (payment) => {
    setConfirmDialog({
      open: true,
      title: 'Retry Payment',
      message: `Are you sure you want to retry payment ${payment.transactionId}?`,
      onConfirm: async () => {
        try {
          // Mock API call
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          setPayments(prev => prev.map(p => 
            p.id === payment.id 
              ? { ...p, status: 'pending', updatedAt: new Date().toISOString() }
              : p
          ));
          
          toast.success('Payment retry initiated');
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          toast.error('Failed to retry payment');
        }
      }
    });
  };

  const handleExportPayments = () => {
    toast.success('Payments exported successfully');
  };

  const columns = [
    {
      field: 'transactionId',
      headerName: 'Transaction ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'customer',
      headerName: 'Customer',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.value.email}
          </Typography>
        </Box>
      )
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          ${params.value.toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {getMethodIcon(params.value)}
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {params.value.replace('_', ' ')}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'gateway',
      headerName: 'Gateway',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata"
          })}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewPayment(params.row)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
          {params.row.status === 'failed' && (
            <Tooltip title="Retry Payment">
              <IconButton size="small" onClick={() => handleRetryPayment(params.row)}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status === 'completed' && params.row.refundedAmount === 0 && (
            <Tooltip title="Process Refund">
              <IconButton size="small" onClick={() => handleRefundPayment(params.row)}>
                <MoneyIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  const filters = [
    {
      key: 'status',
      label: 'Status',
      value: statusFilter,
      options: [
        { value: '', label: 'All Status' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
        { value: 'refunded', label: 'Refunded' },
        { value: 'cancelled', label: 'Cancelled' }
      ]
    },
    {
      key: 'method',
      label: 'Payment Method',
      value: methodFilter,
      options: [
        { value: '', label: 'All Methods' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'paypal', label: 'PayPal' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'cash', label: 'Cash' }
      ]
    },
    {
      key: 'date',
      label: 'Date Range',
      value: dateFilter,
      options: [
        { value: '', label: 'All Dates' },
        { value: 'today', label: 'Today' },
        { value: 'week', label: 'This Week' },
        { value: 'month', label: 'This Month' },
        { value: 'year', label: 'This Year' }
      ]
    }
  ];

  const handleFilterChange = (key, value) => {
    if (key === 'status') setStatusFilter(value);
    if (key === 'method') setMethodFilter(value);
    if (key === 'date') setDateFilter(value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setMethodFilter('');
    setDateFilter('');
  };

  if (loading) {
    return <LoadingSpinner message="Loading payments..." />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Payment & Transaction Management
      </Typography>

      {/* Payment Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6">
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    ${stats.totalRevenue.toLocaleString()}
                  </Typography>
                </Box>
                <MoneyIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6">
                    Transactions
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {stats.totalTransactions.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6">
                    Success Rate
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {stats.successRate}%
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6">
                    Failed
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {stats.failedPayments}
                  </Typography>
                </Box>
                <ErrorIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6">
                    Pending
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    {stats.pendingPayments}
                  </Typography>
                </Box>
                <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6">
                    Refunded
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold">
                    ${stats.refundedAmount.toLocaleString()}
                  </Typography>
                </Box>
                <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <SearchAndFilter
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search transactions, customers, or transaction IDs..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Action Buttons */}
      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchPayments}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportPayments}
        >
          Export Payments
        </Button>
      </Box>

      {/* Payments Table */}
      <Card>
        <CardContent>
          <DataGrid
            rows={payments}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            sx={{ border: 0 }}
          />
        </CardContent>
      </Card>

      {/* Payment Details Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Payment Details - {selectedPayment?.transactionId}
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Transaction Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Transaction ID" 
                      secondary={selectedPayment.transactionId} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Order ID" 
                      secondary={selectedPayment.orderId} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Amount" 
                      secondary={`$${selectedPayment.amount.toFixed(2)}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Payment Method" 
                      secondary={selectedPayment.method.replace('_', ' ')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Status" 
                      secondary={
                        <Chip 
                          label={selectedPayment.status} 
                          color={getStatusColor(selectedPayment.status)}
                          size="small"
                        />
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Gateway Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Gateway" 
                      secondary={selectedPayment.gateway} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Gateway Transaction ID" 
                      secondary={selectedPayment.gatewayTransactionId} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Fees" 
                      secondary={`$${selectedPayment.fees.toFixed(2)}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Net Amount" 
                      secondary={`$${selectedPayment.netAmount.toFixed(2)}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Currency" 
                      secondary={selectedPayment.currency} 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              {selectedPayment.refundedAmount > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <AlertTitle>Refund Information</AlertTitle>
                    <Typography>
                      Refunded Amount: ${selectedPayment.refundedAmount.toFixed(2)}
                    </Typography>
                    {selectedPayment.refundReason && (
                      <Typography>
                        Reason: {selectedPayment.refundReason}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}
              
              {selectedPayment.failureReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <AlertTitle>Failure Reason</AlertTitle>
                    <Typography>{selectedPayment.failureReason}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)}>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Refund Amount"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            type="number"
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Refund Reason"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleProcessRefund}>
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
    </Box>
  );
};

export default Payments;
